import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const DEFAULT_PENDING_MESSAGE = 'Your detailed interview report is being prepared. This is taking longer than usual, but your interview has been saved safely.';

const ReportLoading = ({ takingLong = false, message = DEFAULT_PENDING_MESSAGE }) => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);

  const steps = [
    { label: 'Transcript Processed' },
    { label: 'Code Quality Evaluated' },
    { label: 'Measuring Communication' },
    { label: 'Benchmarking' },
    { label: 'Generating Recommendations' }
  ];

  const displayedStep = takingLong ? steps.length - 1 : step;
  const displayedProgress = takingLong ? Math.max(progress, 98) : progress;

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
    }, 3600);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 98) return prev;
        return prev + 0.5;
      });
    }, 90);

    const timerInterval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
      clearInterval(timerInterval);
    };
  }, [steps.length]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 relative">
      <div className="max-w-md w-full flex flex-col items-center text-center">

       
        <h2 className="text-base font-semibold text-slate-700 mb-2">
          We're preparing your report.
        </h2>


        <div className="flex flex-col items-center mb-8 w-full max-w-[280px]">
          <div className="flex items-center w-full gap-4">
            <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
              <div
                className="h-full bg-slate-900 transition-all duration-300 ease-out rounded-full relative"
                style={{ width: `${displayedProgress}%` }}
              >
                 <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
              </div>
            </div>
            <span className="text-xs font-bold text-slate-900 w-8 text-right">{Math.floor(displayedProgress)}%</span>
          </div>
        </div>

        {/* <div className="w-full h-px bg-slate-200 mb-8" /> */}

        <div className="flex flex-col items-start space-y-4 w-full max-w-[280px] mx-auto text-xs font-medium mb-8">
          {steps.map((s, idx) => {
            const isActive = idx === displayedStep;
            const isCompleted = idx < displayedStep;
            const isPending = idx > displayedStep;

            return (
              <div key={idx} className={`flex items-center gap-4 transition-colors duration-300 ${
                isCompleted ? 'text-emerald-600' : isActive ? 'text-slate-900' : 'text-slate-400'
              }`}>
                <div className="w-5 flex justify-center shrink-0">
                  {isCompleted && <span className="text-base leading-none">✓</span>}
                  {isActive && <span className="text-base leading-none animate-spin inline-block">⟳</span>}
                  {isPending && <span className="text-base leading-none">○</span>}
                </div>
                <span>{s.label}</span>
              </div>
            );
          })}
        </div>

        <div className="w-full h-px bg-slate-200 mb-8" />

        {takingLong ? (
            <div className="flex flex-col items-center text-xs font-medium">
                <span className="text-emerald-600 mb-1 font-bold">Report generation queued</span>
                <span className="text-slate-500 max-w-[300px] leading-relaxed">
                    This is taking longer than usual, but your interview has been saved safely.
                </span>
            </div>
        ) : (
            <div className="flex flex-col items-center text-xs font-medium">
                <span className="text-slate-500 mb-1">Estimated time remaining</span>
                <span className="text-slate-900 font-bold">{timeLeft} seconds</span>
            </div>
        )}

      </div>
    </div>
  );
};

export default ReportLoading;
